import {
  DeleteOutlined,
  DownloadOutlined,
  FileImageOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Modal, Popconfirm, Row, Spin, Typography } from "antd";
import { useEffect, useState } from "react";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";

import { deleteObject, getDownloadURL, ref } from "firebase/storage";
import { useAuth } from "../AuthProvider/AuthProvider";
import { db, storage } from "../firebase";

const { Title, Text } = Typography;

type ResourceType = "image" | "video";

type Resource = {
  id: string;
  title?: string;
  description?: string;
  thumbnailPathUrl?: string | null;
  thumbnailDownloadUrl?: string | null;
  thumbnailUrl?: string;
  videoPathUrl?: string;
  videoDownloadUrl?: string;
  videoFileName?: string;
  resourceCollection?: string;
  resourceType?: ResourceType;
  contentType?: string | null;
};

const isImageResource = (resource: Resource) =>
  resource.resourceType === "image" || resource.contentType?.startsWith("image/");

const isVideoResource = (resource: Resource) =>
  resource.resourceType === "video" ||
  resource.contentType?.startsWith("video/") ||
  (!isImageResource(resource) && !!resource.videoPathUrl);

async function resolveStorageUrl(storagePathOrUrl: string) {
  if (storagePathOrUrl.startsWith("http")) return storagePathOrUrl;
  return getDownloadURL(ref(storage, storagePathOrUrl));
}

async function downloadFile(storagePathOrUrl: string, filename: string) {
  const url = await resolveStorageUrl(storagePathOrUrl);

  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "resource";
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(objectUrl);
}

async function deleteResource(
  resourceId: string,
  resourcePath?: string,
  thumbnailPath?: string | null,
) {
  try {
    if (resourcePath) {
      await deleteObject(ref(storage, resourcePath));
    }

    // Image resources may use the same file as their thumbnail. Avoid deleting twice.
    if (thumbnailPath && thumbnailPath !== resourcePath) {
      await deleteObject(ref(storage, thumbnailPath));
    }

    await deleteDoc(doc(db, "resources", resourceId));

    return true;
  } catch (err) {
    console.error("Delete failed:", err);
    throw err;
  }
}

export default function ResourceGalleryPage() {
  const { currentRole, currentUser } = useAuth();

  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isAdmin = currentRole?.admin === true;

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      setLoading(false);
      return;
    }

    const loadResources = async () => {
      try {
        setLoading(true);

        const q = query(collection(db, "resources"), orderBy("createdAt", "desc"));

        const snapshot = await getDocs(q);

        const items: Resource[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Resource, "id">),
        }));

        const withThumbnails = await Promise.all(
          items.map(async (item) => {
            const thumbnailSource = item.thumbnailDownloadUrl || item.thumbnailPathUrl;

            if (!thumbnailSource) return item;

            try {
              return {
                ...item,
                thumbnailUrl: await resolveStorageUrl(thumbnailSource),
              };
            } catch (err) {
              console.warn("Failed to load thumbnail:", thumbnailSource, err);
              return item;
            }
          }),
        );

        setResources(withThumbnails);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [currentUser, isAdmin]);

  const openVideoPreview = async (resource: Resource) => {
    if (!isVideoResource(resource) || !resource.videoPathUrl) return;

    try {
      setPreviewResource(resource);
      setPreviewLoading(true);
      setPreviewUrl(await resolveStorageUrl(resource.videoDownloadUrl || resource.videoPathUrl));
    } catch (err) {
      console.error("Failed to open preview:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewResource(null);
    setPreviewUrl(null);
    setPreviewLoading(false);
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#1f1e23]">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-[#1f1e23] text-white p-6 text-center">
        <Title level={2} style={{ color: "white" }}>
          Admin Only
        </Title>
        <Text style={{ color: "rgba(255,255,255,.65)" }}>
          You need an administrator account to view the resource gallery.
        </Text>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-col items-center bg-[#1f1e23] text-white overflow-auto p-6">
      <Title level={2} style={{ color: "white" }}>
        Resource Gallery
      </Title>

      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        {resources.map((resource) => {
          const imageResource = isImageResource(resource);
          const videoResource = isVideoResource(resource);
          const downloadPath = resource.videoDownloadUrl || resource.videoPathUrl;
          const filename = resource.videoFileName || resource.title || "resource";

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={resource.id}>
              <Card
                hoverable
                cover={
                  resource.thumbnailUrl ? (
                    <div
                      role={videoResource ? "button" : undefined}
                      tabIndex={videoResource ? 0 : undefined}
                      onClick={() => openVideoPreview(resource)}
                      onKeyDown={(e) => {
                        if (videoResource && (e.key === "Enter" || e.key === " ")) {
                          openVideoPreview(resource);
                        }
                      }}
                      className="relative"
                      style={{ cursor: videoResource ? "pointer" : "default" }}
                    >
                      <img
                        alt={resource.title || "Resource thumbnail"}
                        src={resource.thumbnailUrl}
                        style={{
                          width: "100%",
                          objectFit: "cover",
                          aspectRatio: "16/9",
                        }}
                      />
                      {videoResource && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 text-white text-5xl">
                          <PlayCircleOutlined />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => openVideoPreview(resource)}
                      style={{
                        height: 180,
                        background: "#111",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: videoResource ? "pointer" : "default",
                        fontSize: 40,
                      }}
                    >
                      {videoResource ? <PlayCircleOutlined /> : <FileImageOutlined />}
                    </div>
                  )
                }
              >
                <Card.Meta
                  title={resource.title || "Untitled"}
                  description={
                    <Text type="secondary">
                      {imageResource ? "Image" : "Video"} · Collection: {resource.resourceCollection}
                    </Text>
                  }
                />

                {resource.description && (
                  <div style={{ marginTop: 8 }}>
                    <Text>{resource.description}</Text>
                  </div>
                )}

                <div className="flex justify-end mt-4 gap-2">
                  <Button
                    type="primary"
                    disabled={!downloadPath}
                    onClick={() => downloadPath && downloadFile(downloadPath, filename)}
                  >
                    <DownloadOutlined />
                  </Button>
                  <Popconfirm
                    title="Are you sure you want to delete this resource?"
                    onConfirm={() =>
                      deleteResource(
                        resource.id,
                        resource.videoPathUrl,
                        resource.thumbnailPathUrl,
                      ).then(() =>
                        setResources((prev) => prev.filter((item) => item.id !== resource.id)),
                      )
                    }
                  >
                    <Button type="primary" danger>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        open={!!previewResource}
        title={previewResource?.title || "Video Preview"}
        footer={null}
        onCancel={closePreview}
        width={900}
        destroyOnClose
      >
        {previewLoading && (
          <div className="flex justify-center p-8">
            <Spin />
          </div>
        )}
        {previewUrl && (
          <video
            src={previewUrl}
            controls
            playsInline
            style={{ width: "100%", maxHeight: "70vh", background: "black" }}
          />
        )}
      </Modal>
    </div>
  );
}
